import React from 'react';
import type { ReactNode } from 'react';

import { Box, Chip, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

export interface RoomMappingRow {
  chip?: { color?: 'info'; label: string };
  errorText?: string;
  id: string;
  /** The room key currently chosen ('' when none). */
  roomKey: string;
  subtitle?: ReactNode;
  title: string;
}

export interface RoomMappingOption {
  id: string;
  isNew?: boolean;
  name: string;
}

interface Props {
  description: Array<ReactNode>;
  disabled?: boolean;
  onSelectRoom: (rowId: string, roomKey: string) => void;
  roomOptions: Array<RoomMappingOption>;
  rows: Array<RoomMappingRow>;
  selectIdPrefix: string;
  title: string;
}

export default function RoomMappingPicker({
  description,
  disabled,
  onSelectRoom,
  roomOptions,
  rows,
  selectIdPrefix,
  title,
}: Props) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {description.map((paragraph, index) => (
        <Typography key={index} variant="body2" color="text.secondary" gutterBottom>
          {paragraph}
        </Typography>
      ))}

      <Stack spacing={1.5} mt={3}>
        {rows.map((row) => {
          const selectId = `${selectIdPrefix}-${row.id}`;
          // A chosen room that isn't offered reads as unset rather than an invalid value.
          const selectedRoomKey = roomOptions.some((room) => room.id === row.roomKey) ? row.roomKey : '';

          return (
            <Stack key={row.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
              <Stack sx={{ justifyContent: 'center', minHeight: { sm: 40 }, minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2">{row.title}</Typography>
                {row.subtitle}
              </Stack>

              {/* Fixed width, right-aligned, so the chips line up beside the selects whichever row has one. */}
              <Box
                sx={{
                  alignItems: 'center',
                  display: { xs: row.chip ? 'flex' : 'none', sm: 'flex' },
                  flexShrink: 0,
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  minHeight: { sm: 40 },
                  width: { sm: 120 },
                }}
              >
                {row.chip && <Chip label={row.chip.label} color={row.chip.color} size="small" variant="outlined" />}
              </Box>

              <FormControl size="small" error={!!row.errorText} sx={{ width: { xs: '100%', sm: 320 }, flexShrink: 0 }}>
                <InputLabel id={selectId}>Room</InputLabel>
                <Select
                  label="Room"
                  labelId={selectId}
                  value={selectedRoomKey}
                  disabled={disabled}
                  onChange={(event) => onSelectRoom(row.id, event.target.value)}
                >
                  {roomOptions.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.isNew ? `${room.name} (new)` : room.name}
                    </MenuItem>
                  ))}
                </Select>
                {row.errorText && <FormHelperText>{row.errorText}</FormHelperText>}
              </FormControl>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
